import { Icon, Stack } from '@ace-clublabs/shared-component-library';
import { useRef } from 'react';
import SearchResultsList from '@/components/shared/SearchBox/SearchResultsList';
import { useSearchBox } from '@/components/shared/SearchBox/useSearchBox';
import SearchButton from './SearchButton';
import SearchContainer from './SearchContainer';
import SearchTextField from './SearchTextField';
import type { RSSearchBoxProps } from './types';

const RSSearchBox: React.FC<RSSearchBoxProps> = ({
  sx,
  placeholder,
  isWarningColor,
  value,
  selectedResult: controlledSelected,
  onSelect,
  onChange,
  searchResults,
  isLoading = false,
  onContinueClick,
}) => {
  const {
    displayValue,
    selectedResult,
    handleSearchChange,
    handleSelect,
    handleClearSelected,
    handleKeyDown,
    textFieldRef,
    highlightedIndex,
  } = useSearchBox({
    value,
    selectedResult: controlledSelected,
    onSelect,
    onChange,
    searchResults,
    onConfirm: onContinueClick,
  });

  const containerRef = useRef<HTMLDivElement>(null);

  return (
    <>
      <SearchContainer ref={containerRef} sx={sx} data-testid='rs-search-box'>
        <Stack
          direction='row'
          spacing={1}
          sx={(theme) => ({
            mt: 1,
            width: '100%',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 1,
            py: 0.5,
            '&:hover': {
              backgroundColor: 'primary.100',
              '& .MuiInputBase-root': {
                backgroundColor: `${theme.palette.primary[100]} !important`,
              },
            },
          })}
        >
          <SearchTextField
            ref={textFieldRef}
            placeholder={placeholder}
            placeholderColor={isWarningColor ? '#be3317' : undefined}
            value={displayValue}
            onChange={handleSearchChange}
            selectedResult={selectedResult}
            onClearSelected={handleClearSelected}
            onKeyDown={handleKeyDown}
            startAdornment={
              <Icon
                iconName={
                  isWarningColor
                    ? 'triangle-exclamation-regular'
                    : 'magnifying-glass-regular'
                }
                sx={{
                  color: 'grey.400',
                  ml: 1,
                  '& path': {
                    fill: isWarningColor ? '#be3317' : 'grey.400',
                  },
                }}
              />
            }
          />
          <SearchButton onClick={onContinueClick} loading={isLoading} />
        </Stack>
      </SearchContainer>
      <SearchResultsList
        onSelect={handleSelect}
        open={(value !== '' && !!searchResults) || !!searchResults?.length}
        results={searchResults}
        anchorEl={containerRef.current}
        highlightedIndex={highlightedIndex}
      />
    </>
  );
};

export default RSSearchBox;
